import _ from 'lodash';
import React from 'react';
import { RegieContext } from '@/contexts';
import type { FactureType } from '@/types';
import { getOutdatedCycles } from '@/utils';

/**
 * Filtre le tableau de facture donné
 * et renvoi uniquement les factures qui ne sont plus encaissable.
 *
 * Doit être dans un context de régie sélectionnée pour fonctionner.
 */
const useFacturesNonEncaissable = (factures: FactureType[]) => {
  const { selectedRegie, regies } = React.useContext(RegieContext);

  const regie = React.useMemo(
    () => _.find(regies, { identifiant: selectedRegie }),
    [regies, selectedRegie],
  );
  const regieProlongee = React.useMemo(() => regie?.regieProlongee, [regie]);
  const joursEncaissementSupplementaires = React.useMemo(
    () => regie?.joursEncaissementSupplementaires || 0,
    [regie],
  );

  /** Liste des cycles de facturations présents dans les factures */
  const cycles = React.useMemo(() => _.uniqBy(_.map(factures, 'cycle'), 'identifiant'), [factures]);

  /** Liste des cycles de facturation pour lesquels il n'est plus possible
   * d'encaissé de facture si la régie ne l'autorise pas */
  const outdatedCycles = React.useMemo(
    () => getOutdatedCycles(cycles, regieProlongee, joursEncaissementSupplementaires),
    [cycles, joursEncaissementSupplementaires, regieProlongee],
  );

  /** Liste des factures qui ne peuvent plus être encaissées */
  const staledFactures = React.useMemo(
    () =>
      _.filter(factures, (facture) =>
        _.includes(_.map(outdatedCycles, 'identifiant'), facture.cycle.identifiant),
      ),
    [factures, outdatedCycles],
  );

  return staledFactures;
};

export default useFacturesNonEncaissable;
